/**
 * Bridges the room's gameplay protocol to the mahjong client — domino's
 * `NetworkGameController` pattern, trimmed to what T12 needs.
 *
 * The server is fully **authoritative**: the client never runs a transition of
 * its own. Every local action leaves as an intent (`MAHJONG_DISCARD` /
 * `CLAIM` / `PASS` / `TSUMO` / `KONG` / `NEXT_HAND`); the server validates it
 * against the engine and broadcasts the resulting per-seat-filtered
 * `STATE_SYNC` back. Each snapshot is rebuilt into a `MahjongState` by
 * `snapshotToState` (read its header — opponents' hands arrive as PLACEHOLDER
 * tiles) and handed to `onState`, which the scene wires into its own state.
 *
 * The raw snapshot rides along with it: `matchState`, `handScore` and
 * `claimWindow` live above a single hand, so they have no home in
 * `MahjongState` and the scene reads them off the snapshot directly.
 *
 * The *connection* half — disconnect, reconnect, rematch, the terminal — is not
 * a snapshot at all, so it lives in `overlayState.ts`'s machine and this file
 * only translates the wire into it. Resign is deliberately outbound-only: the
 * server turns the resigning seat into a bot (`server/…/SeatPresence.ts`), so
 * the table simply keeps sending snapshots and there is nothing to overlay.
 */

import type { ConnectionManager, RoomClient } from '@bn-games/room-client';
import { SEATS, type Seat } from '../game/types.js';
import type { MahjongState } from '../game/stateCore.js';
import type { KongDecision } from '../ai/types.js';
import type {
  ClientMessage,
  MahjongClaimIntent,
  MahjongSnapshot,
  ServerMessage,
} from './protocol.js';
import { snapshotToState } from './snapshotToState.js';
import { OverlayMachine, type GameOverResult, type OverlayView } from './overlayState.js';

export interface NetworkGameControllerOptions {
  readonly room: RoomClient<ClientMessage, ServerMessage>;
  /** Push a server-authoritative state into the scene. */
  readonly onState: (state: MahjongState, snapshot: MahjongSnapshot) => void;
  /** The table is finished — a concluded Classical match, or a Simplified room's final hand. */
  readonly onGameOver: (
    winnerSeats: readonly Seat[],
    finalScores: readonly number[] | null,
  ) => void;
}

export class NetworkGameController {
  private readonly opts: NetworkGameControllerOptions;
  private readonly connection: ConnectionManager<ClientMessage, ServerMessage>;
  private readonly machine: OverlayMachine;
  private overlayListener: ((view: OverlayView) => void) | null = null;
  private unsubMessage: (() => void) | null = null;
  private unsubState: (() => void) | null = null;

  /**
   * The overlay half of the transport, as an object rather than as methods on
   * the controller: `OnlineDriver` takes it optionally, so a test fake that
   * only exercises intents need not implement any of it.
   */
  readonly overlay = {
    onOverlayChange: (listener: (view: OverlayView) => void): void => {
      this.overlayListener = listener;
      listener(this.machine.view());
    },
    requestRematch: (): void => {
      this.connection.send({ type: 'REMATCH_REQUEST' });
      this.machine.rematchOffered();
    },
    acceptRematch: (): void => {
      this.connection.send({ type: 'REMATCH_ACCEPT' });
      this.machine.dismiss();
    },
    declineRematch: (): void => {
      this.connection.send({ type: 'REMATCH_DECLINE' });
      this.machine.dismiss();
    },
    dismissOverlay: (): void => this.machine.dismiss(),
  };

  constructor(opts: NetworkGameControllerOptions) {
    this.opts = opts;
    this.connection = opts.room.getConnection();
    this.machine = new OverlayMachine(() => this.overlayListener?.(this.machine.view()));
  }

  /**
   * The seat the server sat us in (`GAME_START` / `RECONNECT_OK`), or `null`
   * before the table starts. This is the point of view every renderer rotates
   * around — see `layout/types.ts`'s `seatPosition`.
   */
  get localSeat(): Seat | null {
    const seat = this.opts.room.getInfo().localSeat;
    return SEATS.find((s) => s === seat) ?? null;
  }

  /** The room's code, or `null` before one exists. Half of what re-deriving a
   *  verifiable deal needs (`verify/verifyModel.ts`); the proof is the rest. */
  get roomCode(): string | null {
    return this.opts.room.getInfo().roomCode;
  }

  attach(): void {
    if (this.unsubMessage !== null) return;
    this.unsubMessage = this.connection.onMessage((msg) => this.handleServerMessage(msg));
    this.unsubState = this.connection.onStateChange((state) => {
      if (state === 'disconnected') this.machine.selfDisconnected(Date.now());
      else if (state === 'connected') this.machine.selfConnected();
    });
  }

  detach(): void {
    this.unsubMessage?.();
    this.unsubMessage = null;
    this.unsubState?.();
    this.unsubState = null;
    this.overlayListener = null;
    this.machine.dispose();
  }

  // --- Local intents (client → server) ---

  sendDiscard(tileId: number): void {
    this.connection.send({ type: 'MAHJONG_DISCARD', tileId });
  }

  sendClaim(claim: MahjongClaimIntent): void {
    this.connection.send({ type: 'MAHJONG_CLAIM', claim });
  }

  sendPass(): void {
    this.connection.send({ type: 'MAHJONG_PASS' });
  }

  sendTsumo(): void {
    this.connection.send({ type: 'MAHJONG_TSUMO' });
  }

  sendKong(kong: KongDecision): void {
    this.connection.send({ type: 'MAHJONG_KONG', kong });
  }

  sendNextHand(): void {
    this.connection.send({ type: 'MAHJONG_NEXT_HAND' });
  }

  // --- Internals ---

  private handleServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'STATE_SYNC':
        this.opts.onState(snapshotToState(msg.snapshot), msg.snapshot);
        break;

      case 'MAHJONG_GAME_OVER':
        this.machine.gameOver(this.resultFor(msg.winnerSeats));
        this.opts.onGameOver(msg.winnerSeats, msg.finalScores);
        break;

      case 'OPPONENT_DISCONNECTED':
        this.machine.opponentDisconnected(msg.gracePeriodEndsAt);
        break;
      case 'OPPONENT_RECONNECTED':
        this.machine.opponentReconnected();
        break;
      case 'RECONNECT_OK':
        this.machine.reconnected();
        break;
      case 'RECONNECT_FAIL':
        this.machine.disconnected();
        break;

      case 'REMATCH_REQUEST':
        this.machine.rematchRequested();
        break;
      case 'REMATCH_DECLINE':
        this.machine.rematchDeclined();
        break;
      case 'REMATCH_START':
        this.machine.rematchStarted();
        break;

      default:
        // Lobby traffic — `RoomClient` owns it.
        break;
    }
  }

  /** A four-seat table can end level, and `MatchLayer` reports every seat tied
   *  at the top as a winner — so "we are among several" is a draw, not a win. */
  private resultFor(winnerSeats: readonly Seat[]): GameOverResult {
    const seat = this.localSeat;
    if (seat === null || !winnerSeats.includes(seat)) return 'loss';
    return winnerSeats.length > 1 ? 'draw' : 'win';
  }
}
